import { getCartErrorMessage } from '@theme/cart-feedback';

/**
 * <s-product-form> adds the selected variant to the cart on-page.
 * Every click is confirmed instantly ("Added to cart ✓" + toast) and queued,
 * so shoppers can add several items quickly. The cart request runs in the
 * background and only shows a message if it fails,
 * and the page stays put (no drawer pop-up) so shopping can continue.
 */
class SProductForm extends HTMLElement {
  #ac;
  #queue = Promise.resolve();
  #pending = 0;
  #labelTimer;
  #labelButton;
  #labelOriginal;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      const form = event.target;
      const submitter = event.submitter instanceof HTMLElement ? event.submitter : form.querySelector('button[name="add"]');
      if (submitter?.getAttribute('aria-disabled') === 'true') return;

      // Read the line at click time so a later variant/quantity change cannot alter a queued add.
      const line = this.#lineFromFormData(new FormData(form));
      if (!line) return;

      this.#pending += 1;
      this.setAttribute('aria-busy', 'true');
      this.#showError('');

      // Confirm instantly; the cart request finishes in the background and only
      // interrupts the shopper if it fails.
      this.#setLabel(submitter, this.dataset.addedLabel);
      this.#labelTimer = setTimeout(() => this.#restoreLabel(), 1500);
      if (this.dataset.addedMessage) {
        document.dispatchEvent(new CustomEvent('s-notification:add', {
          detail: { message: this.dataset.addedMessage, variant: 'success', duration: 2500 },
        }));
      }
      this.#queue = this.#queue.then(() => this.#submit(line, submitter));
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#labelTimer);
    this.#restoreLabel();
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // Without the cart add route, leave the Shopify product form native.
    if (!this.dataset.cartAddUrl) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(line, submitter) {
    const signal = this.#ac?.signal;
    if (signal?.aborted) return;

    try {
      // Ajax cart add endpoint (route written by Liquid into data-cart-add-url).
      const item = { id: Number(line.merchandiseId), quantity: line.quantity };
      if (line.sellingPlanId) item.selling_plan = Number(line.sellingPlanId);
      if (line.attributes?.length) {
        item.properties = Object.fromEntries(line.attributes.map(({ key, value }) => [key, value]));
      }

      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
        signal,
      });
      const data = await response.json().catch(() => ({}));

      if (signal?.aborted) return;

      if (!response.ok) {
        this.#failed(data?.description || data?.message || this.dataset.errorMessage || '');
        return;
      }

      // Only count stock down once the cart has confirmed the add.
      this.#reduceStock(line.quantity, submitter);

      try {
        await customElements.whenDefined('s-cart');
        await customElements.get('s-cart')?.refreshMountedCarts?.({ signal });
      } catch (refreshError) {
        // The item is in the cart; only the cart display refresh failed.
      }
    } catch (error) {
      if (signal?.aborted) return;

      this.#failed(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.#pending = Math.max(0, this.#pending - 1);
      if (this.#pending === 0) this.removeAttribute('aria-busy');
    }
  }

  #failed(message) {
    clearTimeout(this.#labelTimer);
    this.#restoreLabel();
    this.#showError(message);
  }

  // Temporarily swaps the add button's text (Adding... / Added to cart).
  #setLabel(button, label) {
    if (!(button instanceof HTMLElement) || !label) return;
    clearTimeout(this.#labelTimer);
    if (this.#labelButton !== button) {
      this.#restoreLabel();
      this.#labelButton = button;
      this.#labelOriginal = button.innerHTML;
    }
    button.textContent = label;
  }

  #restoreLabel() {
    if (this.#labelButton instanceof HTMLElement && this.#labelOriginal != null) {
      this.#labelButton.innerHTML = this.#labelOriginal;
    }
    this.#labelButton = null;
    this.#labelOriginal = null;
  }


  // Keeps the stock count and quantity cap in step with what is now in the cart.
  #reduceStock(addedQuantity, submitter) {
    const status = this.querySelector('[data-stock-status]');
    if (!status) return;

    const previous = Number.parseInt(status.dataset.stockRemaining || '0', 10);
    const remaining = Math.max(0, (Number.isFinite(previous) ? previous : 0) - addedQuantity);
    status.dataset.stockRemaining = String(remaining);

    if (remaining > 0) {
      status.textContent = (status.dataset.stockTemplate || '').replace('[qty]', String(remaining));
      status.classList.remove('text-sale');
      status.classList.add('text-on-surface');
    } else {
      status.textContent = status.dataset.stockMaxed || '';
      status.classList.remove('text-on-surface');
      status.classList.add('text-sale');
    }

    const selector = this.querySelector('s-quantity-selector');
    const input = selector?.querySelector('input[type="number"]');
    if (selector && input) {
      const min = Number.parseInt(selector.getAttribute('min') || '1', 10) || 1;
      const max = Math.max(min, remaining);
      selector.setAttribute('max', String(max));
      input.max = String(max);
      input.value = String(min);
      input.dispatchEvent(new Event('change'));
    }

    if (remaining <= 0) {
      const addButton = submitter instanceof HTMLElement ? submitter : this.querySelector('button[name="add"]');
      if (addButton) {
        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');
        addButton.classList.add('opacity-50');
        addButton.classList.remove('cursor-wait');
      }
    }
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
